import DossierSteps from "../DossierSteps";
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, MenuItem, TextField, Typography } from "@mui/material";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import DirectionsCarRoundedIcon from "@mui/icons-material/DirectionsCarRounded";
import MplusApi from "Services/Mplus";
import { documentLabel } from "../DossierDetails";
import {
  apiErrorMessage,
  financingProduct,
  financingReached,
  formatDate,
  formatDateTime,
  isSigned,
  productFeatures,
  productId,
  toAssetUrl,
  unwrap,
  unwrapList,
  useHasRole,
} from "../mplusUtils";
import useStyles from "./style";

// Codes envoyés dans documents_handed → libellés affichés.
const DOCUMENTS = [
  { code: "CARTE_GRISE", label: "Carte grise" },
  { code: "ASSURANCE", label: "Assurance" },
  { code: "CONTRAT", label: "Contrat" },
  { code: "CLES_RECHANGE", label: "Clés de rechange" },
  { code: "MANUEL", label: "Manuel du véhicule" },
];

const locationLabel = (location) => (typeof location === "string" ? location : location?.name || location?.code) || "--";
const isHub = (location) => (location?.kind || "").toString().toUpperCase() === "HUB";

const Delivery = ({ application, financing, notify, onBack, onNext, onReload }) => {
  const classes = useStyles();
  const hasRole = useHasRole();
  const isFleet = hasRole("FLEET_MANAGER");
  const [contracts, setContracts] = useState({ sale: null, guarantee: null });
  const [available, setAvailable] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState("");
  const [documents, setDocuments] = useState(["CARTE_GRISE", "ASSURANCE", "CONTRAT"]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);

  const asset = financing?.asset || null;
  const product = financingProduct(financing, application);
  const modelId = productId(financing, application);

  const load = useCallback(async () => {
    if (!financing?.id) return;
    setLoading(true);
    try {
      const data = unwrap(await MplusApi.getFinancingContracts(financing.id)) || {};
      setContracts({ sale: data.sale_contract || null, guarantee: data.guarantee_contract || null });
    } catch (error) {
      setContracts({ sale: null, guarantee: null });
    }
    // Véhicules disponibles du même modèle, pour réserver si besoin.
    if (!financing.asset && modelId) {
      try {
        setAvailable(unwrapList(await MplusApi.getStock({ status: "AVAILABLE", product_catalog_id: modelId, page: 1, limit: 50 })));
      } catch (error) {
        setAvailable([]);
      }
    }
    setLoading(false);
  }, [financing, modelId]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (label, request, success) => {
    setBusy(label);
    try {
      await request();
      notify(success, "success");
      await onReload();
      return true;
    } catch (error) {
      notify(apiErrorMessage(error, "L'opération a échoué."), "error");
      return false;
    } finally {
      setBusy("");
    }
  };

  const handleReserve = () => run("reserve", () => MplusApi.runFinancingAction(financing.id, "reserve", { asset_id: selectedAsset }), "Véhicule réservé pour 48 h.");

  const handleRelease = () => run("release", () => MplusApi.runFinancingAction(financing.id, "release-asset", { reason: "Changement de véhicule depuis l'écran de livraison" }), "Véhicule libéré.");

  const handleDeliver = async () => {
    if (!isFleet || !ready || !asset?.id || delivered || busy) return;
    const ok = await run(
      "deliver",
      () => MplusApi.runFinancingAction(financing.id, "deliver", { asset_id: asset.id, note: note.trim() || undefined, documents_handed: documents }),
      "Livraison confirmée : le remboursement démarre."
    );
    if (ok) onNext();
  };

  const toggleDocument = (document) => {
    setDocuments((previous) => (previous.includes(document) ? previous.filter((item) => item !== document) : [...previous, document]));
  };

  const { sale, guarantee } = contracts;
  const delivered = financingReached(financing?.status, "ACTIVE");
  const checklist = [
    { label: "Financement approuvé", done: financingReached(financing?.status, "LOAN_APPROVED"), date: financing?.loan_approved_at || financing?.approved_at, verb: "Validé" },
    { label: "Contrat de vente signé", done: isSigned(sale), date: sale?.signed_at, verb: "Signé" },
    guarantee ? { label: "Caution signée", done: isSigned(guarantee), date: guarantee?.signed_at, verb: "Signée" } : null,
    { label: "Véhicule réservé", done: Boolean(asset), date: asset?.reserved_until, verb: "Jusqu'au", withTime: true },
    { label: "Véhicule dans un hub", done: isHub(asset?.location), detail: asset ? locationLabel(asset.location) : null },
  ].filter(Boolean);
  const ready = checklist.every((item) => item.done);
  const vehicleImage = toAssetUrl(product?.image_url);
  const tags = [...productFeatures(product), asset?.color].filter(Boolean);
  // Lieu : remise effectuée, sinon hub prévu au dossier, sinon emplacement du véhicule.
  const deliveryPlace = financing?.delivery?.hub || financing?.delivery_location || asset?.location;
  const delivery = financing?.delivery || {};

  return (
    <Box className={classes.page}>
      <Box className={classes.header}>
        <Box className={classes.titleArea}>
          <Box className={classes.breadcrumb}>Gestion des dossiers <span>›</span> {application?.reference}</Box>
          <Box className={classes.headingLine}>
            <Typography className={classes.pageTitle}>Dossier {financing?.reference || application?.reference}</Typography>
            <span className={classes.readyBadge}>{delivered ? "Livré" : ready ? "Prêt à livrer" : "Livraison à préparer"}</span>
          </Box>
        </Box>
        <DossierSteps activeStep={4} completedThrough={delivered ? 4 : 3} />
      </Box>

      {loading ? (
        <Box className={classes.loaderBox}><CircularProgress size={28} /></Box>
      ) : (
        <>
          <Box className={classes.topGrid}>
            <Box className={classes.card}>
              <Typography className={classes.sectionTitle}>Checklist avant livraison</Typography>
              {checklist.map((item) => (
                <Box className={classes.checkRow} key={item.label}>
                  {item.done ? <CheckCircleRoundedIcon /> : <RadioButtonUncheckedRoundedIcon className={classes.todoIcon} />}
                  <b>{item.label}</b>
                  <span>
                    {!item.done ? "À faire" : item.detail || (item.date ? `${item.verb} ${item.withTime ? formatDateTime(item.date) : `le ${formatDate(item.date)}`}` : "Fait")}
                  </span>
                </Box>
              ))}
            </Box>

            <Box className={classes.card}>
              <Typography className={classes.sectionTitle}>Véhicule correspondant (stock)</Typography>
              <Box className={classes.vehicleContent}>
                <Box className={classes.vehicleVisual}>
                  {vehicleImage ? <img src={vehicleImage} alt={product?.name || "Véhicule"} /> : <DirectionsCarRoundedIcon className={classes.vehiclePlaceholder} />}
                  {asset && <span className={classes.stockBadge}>{isHub(asset.location) ? "Au hub" : "Réservé"}</span>}
                </Box>
                <Box className={classes.vehicleInfo}>
                  <Typography className={classes.vehicleName}>{product?.name || application?.vehicleMake || "Véhicule"}</Typography>
                  {tags.length > 0 && <Box className={classes.vehicleTags}>{tags.map((tag) => <span key={tag}>{tag}</span>)}</Box>}
                  {asset ? (
                    <>
                      <Box className={classes.vehicleSpec}><span>Immatriculation</span><b>{asset.plate || "--"}</b></Box>
                      <Box className={classes.vehicleSpec}><span>VIN</span><b>{asset.vin || "--"}</b></Box>
                      <Box className={classes.vehicleSpec}><span>Emplacement</span><b>{locationLabel(asset.location)}</b></Box>
                      {asset.odometer_km != null && <Box className={classes.vehicleSpec}><span>Kilométrage</span><b>{asset.odometer_km} km</b></Box>}
                      {!delivered && isFleet && (
                        <Button className={classes.linkButton} disabled={Boolean(busy)} onClick={handleRelease}>
                          {busy === "release" ? "Libération…" : "Libérer ce véhicule"}
                        </Button>
                      )}
                    </>
                  ) : (
                    <>
                      <Typography className={classes.hint}>
                        {available.length ? `${available.length} véhicule(s) disponible(s) pour ce modèle.` : "Aucun véhicule disponible pour ce modèle : réceptionnez-en un dans le stock."}
                      </Typography>
                      {available.length > 0 && isFleet && (
                        <>
                          <TextField select size="small" fullWidth value={selectedAsset} SelectProps={{ displayEmpty: true }} onChange={(event) => setSelectedAsset(event.target.value)}>
                            <MenuItem value="" disabled>Choisir un véhicule</MenuItem>
                            {available.map((vehicle) => (
                              <MenuItem key={vehicle.id} value={vehicle.id}>
                                {[vehicle.plate, vehicle.vin, locationLabel(vehicle.location)].filter((value) => value && value !== "--").join(" · ")}
                              </MenuItem>
                            ))}
                          </TextField>
                          <Button className={classes.confirmButton} disabled={!selectedAsset || Boolean(busy)} onClick={handleReserve}>
                            {busy === "reserve" ? "Réservation…" : "Réserver (48 h)"}
                          </Button>
                        </>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          <Box className={`${classes.card} ${classes.summaryCard}`}>
            <Typography className={classes.sectionTitle}>Récapitulatif de la livraison</Typography>
            <Box className={classes.summaryRow}><PersonOutlineRoundedIcon /><b>Bénéficiaire</b><span>{application?.driverName}{application?.driverPhone ? ` (${application.driverPhone})` : ""}</span></Box>
            <Box className={classes.summaryRow}><EventAvailableRoundedIcon /><b>Date de livraison</b><span>{delivered ? formatDate(delivery.date || delivery.delivered_at) : `${formatDate(new Date())} (à la confirmation)`}</span></Box>
            <Box className={classes.summaryRow}><LocationOnOutlinedIcon /><b>Lieu de livraison</b><span>{locationLabel(deliveryPlace)}{financing?.planned_delivery_date && !delivered ? ` · prévue le ${formatDate(financing.planned_delivery_date)}` : ""}</span></Box>
            <Box className={classes.summaryRow}>
              <DescriptionOutlinedIcon /><b>Documents remis</b>
              {delivered ? (
                <span>{(delivery.documents_handed || []).map(documentLabel).join(", ") || "--"}</span>
              ) : (
                <Box className={classes.documentChecks}>
                  {DOCUMENTS.map((document) => (
                    <FormControlLabel key={document.code} control={<Checkbox size="small" checked={documents.includes(document.code)} onChange={() => toggleDocument(document.code)} />} label={document.label} />
                  ))}
                </Box>
              )}
            </Box>
            {!delivered && (
              <TextField className={classes.noteField} fullWidth size="small" label="Note de livraison (facultatif)" value={note} onChange={(event) => setNote(event.target.value)} />
            )}
          </Box>

          {!delivered && (!isFleet || !ready) && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {!isFleet
                ? "La confirmation de livraison nécessite le rôle Gestionnaire de flotte (FLEET_MANAGER) ou Administrateur."
                : `Avant de confirmer la livraison, complétez les éléments suivants : ${checklist.filter((item) => !item.done).map((item) => item.label.toLowerCase()).join(", ")}.`}
            </Alert>
          )}

          <Box className={classes.footer}>
            <Box className={classes.repaymentNotice}><InfoOutlinedIcon /><span>Le remboursement débute après la livraison du véhicule : la livraison décaisse le prêt dans Fineract.</span></Box>
            <Box className={classes.actions}>
              <Button className={classes.backButton} startIcon={<ArrowBackRoundedIcon />} onClick={onBack}>Retour aux contrats</Button>
              {delivered ? (
                <Button className={classes.confirmButton} endIcon={<ArrowForwardRoundedIcon />} onClick={onNext}>Suivi des paiements</Button>
              ) : (
                <Button className={classes.confirmButton} endIcon={<ArrowForwardRoundedIcon />} disabled={!isFleet || !ready || Boolean(busy)} onClick={handleDeliver}>
                  {busy === "deliver" ? "Confirmation…" : "Confirmer la livraison"}
                </Button>
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Delivery;
